import "./KcApp.css";
import { lazy, Suspense } from "react";
import DefaultPage from "keycloakify/login/DefaultPage";
import { i18nBuilder } from "keycloakify/login";
import UserProfileFormFields from "keycloakify/login/UserProfileFormFields";
import Template from "./Template";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";

const { useI18n } = i18nBuilder.build();

// Create a dedicated theme instance for Keycloak to ensure isolation
// We try to match the app's theme but keep it self-contained
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: "#0087B4", // FederateX teal-blue
      light: "#7dd3fc", // sky-300 accent
      500: "#0087B4",
      600: "#00769d",
    },
    background: {
      default: "#0f172a", // Slate 900
      paper: "#1e293b",
    },
    text: {
      primary: "#f1f5f9",
      secondary: "#94a3b8",
    },
  },
  typography: {
    fontFamily: ["Inter", "sans-serif"].join(","),
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(4px)",
            borderRadius: "12px",
            "& fieldset": {
              borderColor: "rgba(255, 255, 255, 0.1)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(125, 211, 252, 0.5)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#7dd3fc",
            },
            "& input": {
                color: "white", 
            }
          },
          "& .MuiInputLabel-root": {
             color: "#94a3b8"
          },
          "& .MuiInputLabel-root.Mui-focused": {
             color: "#7dd3fc"
          }
        },
      },
    },
  },
});

const Login = lazy(() => import("./pages/Login"));
const LoginEmail = lazy(() => import("./pages/LoginEmail"));
const LoginOtp = lazy(() => import("./pages/LoginOtp"));
const LoginResetPassword = lazy(() => import("./pages/LoginResetPassword"));
const LoginUpdatePassword = lazy(() => import("./pages/LoginUpdatePassword"));
// If you implement other pages (Register, Terms, etc.) import them here

export default function KcAppImpl(props) {
  const { kcContext } = props;

  // We explicitly assume en for now, or letting keycloakify handle it
  // In a real app we might pass in extra translations
  const { i18n } = useI18n({ kcContext });

  if (i18n === null) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
        <CssBaseline />
        <Suspense fallback={<div className="kc-loading">Loading...</div>}>
        {(() => {
            switch (kcContext.pageId) {
            case "login.ftl":
                return (
                <Login
                    {...{ kcContext, i18n, classes: kcContext.message?.type ? { kcBodyClass: "my-custom-body-class" } : undefined }}
                    Template={Template}
                    doUseDefaultCss={false} // We typically want default keycloak css for layout basics unless we replace EVERYTHING
                />
                );
            case "login-email.ftl":
                // Per-login Email-OTP page (netzbegruenung authenticator).
                return (
                <LoginEmail
                    {...{ kcContext, i18n }}
                    Template={Template}
                    doUseDefaultCss={false}
                />
                );
            case "login-otp.ftl":
                // Authenticator-app (TOTP) page (Keycloak auth-otp-form).
                return (
                <LoginOtp
                    {...{ kcContext, i18n }}
                    Template={Template}
                    doUseDefaultCss={false}
                />
                );
            case "login-reset-password.ftl":
                // Forgot-password request page (reset-credentials flow).
                return (
                <LoginResetPassword
                    {...{ kcContext, i18n }}
                    Template={Template}
                    doUseDefaultCss={false}
                />
                );
            case "login-update-password.ftl":
                // Set-new-password page (reset link + UPDATE_PASSWORD action).
                return (
                <LoginUpdatePassword
                    {...{ kcContext, i18n }}
                    Template={Template}
                    doUseDefaultCss={false}
                />
                );
            // Example of how to add other pages:
            // case "register.ftl": return <Register {...{ kcContext, i18n, Template, doUseDefaultCss: true }} />;
            default:
                // Fallback to default Keycloakify implementation for pages we haven't customized yet
                return (
                <DefaultPage
                    {...{ kcContext, i18n }}
                    Template={Template}
                    doUseDefaultCss={false}
                    UserProfileFormFields={UserProfileFormFields}
                    doMakeUserConfirmPassword={false}
                />
                );
            }
        })()}
        </Suspense>
    </ThemeProvider>
  );
}
